import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../services/api';
import { format } from 'date-fns';

const PaymentStatus = () => {
  const [orderType, setOrderType] = useState('purchase');
  const [selectedOrder, setSelectedOrder] = useState('');
  const [orders, setOrders] = useState([]);
  const [orderDetails, setOrderDetails] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Payment Detail Dialog
  const [openPaymentDetailDialog, setOpenPaymentDetailDialog] = useState(false);
  const [selectedPaymentDetail, setSelectedPaymentDetail] = useState(null);

  const SETTLEMENT_LABELS = {
    full: 'Full Payment',
    partial: 'Partial Payment',
    final: 'Final Payment',
    advance: 'Advance Payment'
  };

  // Fetch orders when component mounts and when order type changes
  useEffect(() => {
    fetchOrders();
  }, [orderType]);

  // Fetch order details when selected order changes
  useEffect(() => {
    if (selectedOrder) {
      fetchOrderDetails(selectedOrder);
      fetchPaymentHistory(selectedOrder);
    } else {
      setOrderDetails(null);
      setPayments([]);
    }
  }, [selectedOrder]);

  const fetchOrders = async () => {
    try {
      setLoadingOrders(true);
      setError(null);
      
      let response;
      if (orderType === 'sales') {
        response = await api.get('/sales/orders/');
      } else {
        response = await api.get('/procurement/orders/');
      }
      
      console.log('Orders Response:', response.data);
      
      let ordersData = [];
      if (Array.isArray(response.data)) {
        ordersData = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        ordersData = response.data.results;
      } else if (response.data && typeof response.data === 'object') {
        ordersData = [response.data];
      }
      
      setOrders(ordersData);
      
      if (ordersData.length > 0 && !selectedOrder) {
        setSelectedOrder(ordersData[0].id);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(`Failed to fetch ${orderType === 'sales' ? 'Sales' : 'Purchase'} orders.`);
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      setLoading(true);
      setError(null);
      
      const endpoint = orderType === 'sales' 
        ? `/sales/orders/${orderId}/` 
        : `/procurement/orders/${orderId}/`;
      
      console.log('Fetching order details from:', endpoint);
      const response = await api.get(endpoint);
      console.log('Order Details Response:', JSON.stringify(response.data, null, 2));
      
      setOrderDetails(response.data);
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError('Failed to fetch order details.');
      setOrderDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async (orderId) => {
    try {
      const endpoint = orderType === 'sales'
        ? `/finance/payments/?sales_order=${orderId}`
        : `/finance/payments/?purchase_order=${orderId}`;
      
      console.log('Fetching payments from:', endpoint);
      const response = await api.get(endpoint);
      console.log('Payments Response:', JSON.stringify(response.data, null, 2));
      
      let paymentsData = [];
      if (Array.isArray(response.data)) {
        paymentsData = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        paymentsData = response.data.results;
      }
      setPayments(paymentsData);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setPayments([]);
    }
  };

  const handleOrderTypeChange = (event) => {
    const newType = event.target.value;
    setOrderType(newType);
    setSelectedOrder('');
    setOrders([]);
    setOrderDetails(null);
    setPayments([]);
    setSearchTerm('');
  };

  const handleOrderSelect = (event) => {
    const orderId = event.target.value;
    setSelectedOrder(orderId);
    setOrderDetails(null);
    setPayments([]);
  };

  const viewPaymentDetail = (payment) => {
    const fetchOrderDetailsFunc = async () => {
      try {
        let orderDetail = null;
        let orderId = null;
        let isSales = false;
        
        if (payment.sales_order) {
          isSales = true;
          if (typeof payment.sales_order === 'object' && payment.sales_order.id) {
            orderId = payment.sales_order.id;
          } else if (typeof payment.sales_order === 'number') {
            orderId = payment.sales_order;
          } else {
            orderId = parseInt(payment.sales_order);
          }
          
          if (orderId && !isNaN(orderId)) {
            const response = await api.get(`/sales/orders/${orderId}/`);
            orderDetail = response.data;
          }
        } else if (payment.purchase_order) {
          isSales = false;
          if (typeof payment.purchase_order === 'object' && payment.purchase_order.id) {
            orderId = payment.purchase_order.id;
          } else if (typeof payment.purchase_order === 'number') {
            orderId = payment.purchase_order;
          } else {
            orderId = parseInt(payment.purchase_order);
          }
          
          if (orderId && !isNaN(orderId)) {
            const response = await api.get(`/procurement/orders/${orderId}/`);
            orderDetail = response.data;
          }
        }
        
        if (orderDetail) {
          const allPaymentsRes = await api.get('/finance/payments/', {
            params: isSales 
              ? { sales_order: orderId }
              : { purchase_order: orderId }
          });
          
          let allPayments = [];
          if (Array.isArray(allPaymentsRes.data)) {
            allPayments = allPaymentsRes.data;
          } else if (allPaymentsRes.data?.results) {
            allPayments = allPaymentsRes.data.results;
          }
          
          allPayments.sort((a, b) => {
            const dateA = new Date(a.payment_date);
            const dateB = new Date(b.payment_date);
            if (dateA < dateB) return -1;
            if (dateA > dateB) return 1;
            return a.id - b.id;
          });
          
          const paymentIndex = allPayments.findIndex(p => p.id === payment.id);
          
          let cumulativePaid = 0;
          for (let i = 0; i <= paymentIndex && i < allPayments.length; i++) {
            cumulativePaid += parseFloat(allPayments[i].amount || 0);
          }
          
          const netAmount = parseFloat(orderDetail.net_amount || orderDetail.total_amount || 0);
          const runningDue = netAmount - cumulativePaid;
          
          setSelectedPaymentDetail({
            ...payment,
            order_value: parseFloat(orderDetail.total_amount || 0),
            tax_amount: parseFloat(orderDetail.gst_amount || orderDetail.tax_amount || orderDetail.total_tax || 0),
            gst_rate_details: orderDetail.gst_rate_details || { rate: orderDetail.gst_rate || 5 },
            cgst_amount: parseFloat(orderDetail.cgst_amount || 0),
            sgst_amount: parseFloat(orderDetail.sgst_amount || 0),
            igst_amount: parseFloat(orderDetail.igst_amount || 0),
            net_amount: netAmount,
            cumulative_amount_paid: cumulativePaid,
            running_due_amount: runningDue,
            order_status: orderDetail.status || 'N/A',
            is_sales_order: isSales,
            order_no: isSales ? orderDetail.so_number : orderDetail.po_number,
            customer: orderDetail.customer,
            supplier: orderDetail.supplier,
            sales_order: payment.sales_order,
            purchase_order: payment.purchase_order,
            items: orderDetail.items || []
          });
          setOpenPaymentDetailDialog(true);
        } else {
          setSelectedPaymentDetail({
            ...payment,
            order_value: 0,
            tax_amount: 0,
            net_amount: 0,
            cumulative_amount_paid: parseFloat(payment.amount || 0),
            running_due_amount: 0,
            order_status: 'N/A',
            is_sales_order: !!payment.sales_order,
            items: []
          });
          setOpenPaymentDetailDialog(true);
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
        setSelectedPaymentDetail({
          ...payment,
          order_value: 0,
          tax_amount: 0,
          net_amount: 0,
          cumulative_amount_paid: parseFloat(payment.amount || 0),
          running_due_amount: 0,
          order_status: 'N/A',
          is_sales_order: !!payment.sales_order,
          items: []
        });
        setOpenPaymentDetailDialog(true);
      }
    };
    
    fetchOrderDetailsFunc();
  };

  const printPaymentReceipt = (payment) => {
    const printWindow = window.open('', '_blank', 'width=650,height=700');
    const partyName = payment.customer?.name || payment.supplier?.name || 'N/A';
    const order = payment.sales_order || payment.purchase_order;
    const orderNo = order?.so_number || order?.po_number || 'N/A';
    const settlementLabel = SETTLEMENT_LABELS[payment.settlement_type] || payment.settlement_type;
    
    const isSales = !!payment.sales_order;
    const orderValue = parseFloat(payment.order_value || 0);
    const netAmount = parseFloat(payment.net_amount || 0);
    const cumulativePaid = parseFloat(payment.cumulative_amount_paid || 0);
    const runningDue = parseFloat(payment.running_due_amount || 0);
    const thisPayment = parseFloat(payment.amount || 0);
    const taxAmount = parseFloat(payment.tax_amount || 0);
    const gstRate = payment.gst_rate_details;
    const cgst = parseFloat(payment.cgst_amount || 0);
    const sgst = parseFloat(payment.sgst_amount || 0);
    const igst = parseFloat(payment.igst_amount || 0);
    const hasGST = gstRate || cgst > 0 || sgst > 0 || igst > 0;
    const paymentDateTime = new Date(payment.payment_date);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Payment Receipt</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; }
          .header { text-align: center; border-bottom: 2px solid #2E7D32; padding-bottom: 20px; margin-bottom: 20px; }
          .header h1 { color: #2E7D32; margin: 0; font-size: 24px; }
          .header p { margin: 5px 0; color: #666; font-size: 14px; }
          .receipt { max-width: 650px; margin: 0 auto; }
          .section { margin: 15px 0; padding: 15px; border-radius: 5px; }
          .section-payment { background: #f5f5f5; }
          .section-order { background: #e8f5e9; }
          .section-balance { background: #e3f2fd; }
          .section-gst { background: #fff3e0; }
          .details table { width: 100%; }
          .details td { padding: 6px 5px; border-bottom: 1px solid #eee; }
          .details .label { font-weight: bold; width: 140px; }
          .amount { font-size: 24px; color: #2E7D32; font-weight: bold; text-align: center; padding: 15px; background: #f5f5f5; border-radius: 5px; margin: 20px 0; }
          .footer { margin-top: 30px; text-align: center; border-top: 1px solid #ddd; padding-top: 20px; color: #666; font-size: 12px; }
          .no-print { display: none; }
          @media print { .no-print { display: none; } body { margin: 20px; } }
          .due-positive { color: #d32f2f; font-weight: bold; }
          .due-zero { color: #2e7d32; font-weight: bold; }
          .section-title { margin-top: 0; margin-bottom: 10px; }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            <h1>Sri Shakthi Traders</h1>
            <p>Cashew Shell Oil Extraction Unit</p>
            <p>GST: 29ABCDE1234F1Z5 | Phone: +91 9876543210</p>
            <p style="font-size:12px;color:#999;">Payment recorded on: ${paymentDateTime.toLocaleString('en-IN', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}</p>
          </div>
          <h2 style="text-align:center;">${payment.payment_type === 'received' ? 'Payment Receipt' : 'Payment Voucher'}</h2>
          
          <div class="section section-payment">
            <h3 class="section-title" style="color:#1976D2;">Payment Details</h3>
            <div class="details">
              <table>
                <tr><td class="label">${payment.payment_type === 'received' ? 'Customer:' : 'Supplier:'}</td><td><strong>${partyName}</strong></td></tr>
                <tr><td class="label">Payment Type:</td><td>${payment.payment_type.toUpperCase()}</td></tr>
                <tr><td class="label">Settlement Type:</td><td><strong>${settlementLabel}</strong></td></tr>
                <tr><td class="label">Amount:</td><td><strong style="color:#2E7D32;font-size:20px;">₹${thisPayment.toFixed(2)}</strong></td></tr>
                <tr><td class="label">Method:</td><td>${payment.payment_method?.replace('_', ' ').toUpperCase() || 'N/A'}</td></tr>
                <tr><td class="label">Date & Time:</td><td>${paymentDateTime.toLocaleString('en-IN', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}</td></tr>
                <tr><td class="label">Reference:</td><td>${payment.reference_number || 'N/A'}</td></tr>
                ${payment.notes ? `<tr><td class="label">Notes:</td><td>${payment.notes}</td></tr>` : ''}
              </table>
            </div>
          </div>

          <div class="section section-order">
            <h3 class="section-title" style="color:#2E7D32;">Order Details</h3>
            <div class="details">
              <table>
                <tr><td class="label">Order No:</td><td><strong>${orderNo}</strong></td></tr>
                <tr><td class="label">Order Type:</td><td>${isSales ? 'Sales Order' : 'Purchase Order'}</td></tr>
                <tr><td class="label">Order Status:</td><td>${payment.order_status || 'N/A'}</td></tr>
                <tr><td class="label">Order Value:</td><td>₹${orderValue.toFixed(2)}</td></tr>
                ${taxAmount > 0 ? `<tr><td class="label">Tax Amount:</td><td>₹${taxAmount.toFixed(2)}</td></tr>` : ''}
                <tr><td class="label">Net Amount:</td><td><strong>₹${netAmount.toFixed(2)}</strong></td></tr>
              </table>
            </div>
          </div>

          <div class="section section-balance">
            <h3 class="section-title" style="color:#1976D2;">Balance at the time of this payment</h3>
            <div class="details">
              <table>
                <tr><td class="label">Cumulative Amount Paid:</td><td><strong style="color:#1976D2;">₹${cumulativePaid.toFixed(2)}</strong></td></tr>
                <tr><td class="label">This Payment:</td><td><strong style="color:#2E7D32;">₹${thisPayment.toFixed(2)}</strong></td></tr>
                <tr><td class="label">Due Amount:</td><td><strong class="${runningDue > 0 ? 'due-positive' : 'due-zero'}">₹${runningDue.toFixed(2)}</strong></td></tr>
              </table>
            </div>
          </div>

          ${hasGST ? `
          <div class="section section-gst">
            <h3 class="section-title" style="color:#ED6C02;">GST Details</h3>
            <div class="details">
              <table>
                ${gstRate ? `<tr><td class="label">GST Rate:</td><td>${gstRate.name || 'GST'} (${gstRate.rate || 0}%)</td></tr>` : ''}
                ${cgst > 0 ? `<tr><td class="label">CGST:</td><td>₹${cgst.toFixed(2)}</td></tr>` : ''}
                ${sgst > 0 ? `<tr><td class="label">SGST:</td><td>₹${sgst.toFixed(2)}</td></tr>` : ''}
                ${igst > 0 ? `<tr><td class="label">IGST:</td><td>₹${igst.toFixed(2)}</td></tr>` : ''}
                ${taxAmount > 0 ? `<tr><td class="label">Total Tax:</td><td><strong>₹${taxAmount.toFixed(2)}</strong></td></tr>` : ''}
              </table>
            </div>
          </div>
          ` : ''}

          <div class="amount">₹${thisPayment.toFixed(2)}</div>

          <div class="footer">
            <p>This is a system generated receipt. No signature required.</p>
            <p>Printed on: ${new Date().toLocaleString('en-IN')}</p>
          </div>

          <div style="text-align:center; margin-top:20px;" class="no-print">
            <button onclick="window.print()" style="padding:10px 30px; background:#2E7D32; color:white; border:none; border-radius:4px; cursor:pointer; font-size:16px;">Print / Download PDF</button>
            <button onclick="window.close()" style="padding:10px 30px; background:#666; color:white; border:none; border-radius:4px; cursor:pointer; font-size:16px; margin-left:10px;">Close</button>
          </div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const getFilteredOrders = () => {
    if (!Array.isArray(orders) || orders.length === 0) {
      return [];
    }
    
    if (!searchTerm) return orders;
    
    const searchStr = searchTerm.toLowerCase();
    return orders.filter(order => {
      const orderNumber = order.order_number || order.po_number || `#${order.id}`;
      const firmName = order.customer?.name || order.supplier?.name || '';
      return orderNumber.toLowerCase().includes(searchStr) || 
             firmName.toLowerCase().includes(searchStr);
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'default',
      'pending': 'warning',
      'approved': 'success',
      'received': 'success',
      'cancelled': 'error',
      'confirmed': 'info',
      'shipped': 'primary',
      'delivered': 'success',
      'partial': 'warning',
      'paid': 'success',
      'unpaid': 'error',
      'overdue': 'error'
    };
    return colors[status?.toLowerCase()] || 'default';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'partial': 'info',
      'paid': 'success',
      'overdue': 'error',
      'cancelled': 'error'
    };
    return colors[status?.toLowerCase()] || 'default';
  };

  // Get product name from item - handles different data structures
  const getProductName = (item) => {
    if (!item) return 'N/A';
    
    // Check if item has product object with name
    if (item.product && typeof item.product === 'object') {
      if (item.product.name) {
        return item.product.name;
      }
      if (item.product.product_name) {
        return item.product.product_name;
      }
      if (item.product.product_code) {
        return item.product.product_code;
      }
    }
    
    // Check if item has direct product_name
    if (item.product_name) {
      return item.product_name;
    }
    
    // Check if item has name
    if (item.name) {
      return item.name;
    }
    
    // Check if item has product_id as number
    if (item.product_id) {
      return `Product #${item.product_id}`;
    }
    
    // If product is a string or number, use it as ID
    if (item.product && typeof item.product === 'number') {
      return `Product #${item.product}`;
    }
    
    return 'N/A';
  };

  const filteredOrders = getFilteredOrders();

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold">
            Payment Status
          </Typography>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={() => { fetchOrders(); if (selectedOrder) { fetchOrderDetails(selectedOrder); fetchPaymentHistory(selectedOrder); } }} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton onClick={() => window.print()} color="primary">
                <PrintIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Order Type</InputLabel>
              <Select
                value={orderType}
                onChange={handleOrderTypeChange}
                label="Order Type"
              >
                <MenuItem value="sales">Sales Orders</MenuItem>
                <MenuItem value="purchase">Purchase Orders</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={`Search ${orderType === 'sales' ? 'Sales' : 'Purchase'} Orders...`}
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Select Order</InputLabel>
              <Select
                value={selectedOrder}
                onChange={handleOrderSelect}
                label="Select Order"
                disabled={loadingOrders || filteredOrders.length === 0}
              >
                <MenuItem value="">
                  <em>
                    {loadingOrders ? 'Loading...' : 
                     filteredOrders.length === 0 ? 'No orders available' : 
                     'Select an order'}
                  </em>
                </MenuItem>
                {filteredOrders.map((order) => {
                  const orderNumber = order.order_number || order.po_number || `#${order.id}`;
                  const firmName = order.customer?.name || order.supplier?.name || 'Unknown Firm';
                  return (
                    <MenuItem key={order.id} value={order.id}>
                      {orderNumber} - {firmName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {loadingOrders && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress size={30} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading orders...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {!loadingOrders && !error && orders.length > 0 && (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              {orders.length} {orderType === 'sales' ? 'Sales' : 'Purchase'} order(s) found
              {searchTerm && ` (${filteredOrders.length} matching)`}
            </Typography>
          </Box>
        )}
      </Paper>

      {loading && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading order details...
          </Typography>
        </Paper>
      )}

      {/* Order Details Section */}
      {selectedOrder && orderDetails && !loading && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Order Details
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Order Information
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body1" component="div">
                      <strong>Order #:</strong> {orderDetails.order_number || orderDetails.po_number || 'N/A'}
                    </Typography>
                    <Typography variant="body1" component="div">
                      <strong>Date:</strong> {orderDetails.order_date || orderDetails.created_at ? format(new Date(orderDetails.order_date || orderDetails.created_at), 'dd/MM/yyyy') : 'N/A'}
                    </Typography>
                    <Typography variant="body1" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <strong>Status:</strong>
                      <Chip 
                        label={orderDetails.status || 'Draft'} 
                        color={getStatusColor(orderDetails.status)}
                        size="small"
                      />
                    </Typography>
                    <Typography variant="body1" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <strong>Payment Status:</strong>
                      <Chip 
                        label={orderDetails.payment_status || 'Pending'} 
                        color={getPaymentStatusColor(orderDetails.payment_status)}
                        size="small"
                      />
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {orderType === 'sales' ? 'Customer' : 'Supplier'} Information
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body1" component="div">
                      <strong>Name:</strong> {orderDetails.customer?.name || orderDetails.supplier?.name || 'N/A'}
                    </Typography>
                    <Typography variant="body1" component="div">
                      <strong>GST:</strong> {orderDetails.customer?.gst_number || orderDetails.supplier?.gst_number || 'N/A'}
                    </Typography>
                    {orderDetails.customer?.phone && (
                      <Typography variant="body1" component="div">
                        <strong>Phone:</strong> {orderDetails.customer.phone}
                      </Typography>
                    )}
                    {orderDetails.supplier?.phone && (
                      <Typography variant="body1" component="div">
                        <strong>Phone:</strong> {orderDetails.supplier.phone}
                      </Typography>
                    )}
                    <Typography variant="body1" component="div">
                      <strong>Total Amount (incl. GST):</strong> {formatCurrency(orderDetails.net_amount || orderDetails.total_amount || 0)}
                    </Typography>
                    <Typography variant="body1" component="div">
                      <strong>Amount Paid:</strong> {formatCurrency(orderDetails.amount_paid || 0)}
                    </Typography>
                    <Typography variant="body1" component="div" fontWeight="bold" color={(orderDetails.net_amount || orderDetails.total_amount || 0) - (orderDetails.amount_paid || 0) > 0 ? 'error' : 'success'}>
                      <strong>Balance Due:</strong> {formatCurrency((orderDetails.net_amount || orderDetails.total_amount || 0) - (orderDetails.amount_paid || 0))}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* GST Details */}
            {(orderDetails.gst_amount > 0 || orderDetails.cgst_amount > 0 || orderDetails.sgst_amount > 0 || orderDetails.igst_amount > 0) && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      GST Details
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {orderDetails.cgst_amount > 0 && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" component="div">
                            <strong>CGST:</strong> {formatCurrency(orderDetails.cgst_amount)} ({orderDetails.cgst_rate || 2.5}%)
                          </Typography>
                        </Grid>
                      )}
                      {orderDetails.sgst_amount > 0 && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" component="div">
                            <strong>SGST:</strong> {formatCurrency(orderDetails.sgst_amount)} ({orderDetails.sgst_rate || 2.5}%)
                          </Typography>
                        </Grid>
                      )}
                      {orderDetails.igst_amount > 0 && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" component="div">
                            <strong>IGST:</strong> {formatCurrency(orderDetails.igst_amount)} ({orderDetails.igst_rate || 0}%)
                          </Typography>
                        </Grid>
                      )}
                      {orderDetails.gst_amount > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="body2" component="div" fontWeight="bold">
                            <strong>Total GST ({orderDetails.gst_rate || 5}%):</strong> {formatCurrency(orderDetails.gst_amount)}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Order Items */}
            {orderDetails.items && Array.isArray(orderDetails.items) && orderDetails.items.length > 0 && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Order Items
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Product</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">GST</TableCell>
                            <TableCell align="right">Total (incl. GST)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orderDetails.items.map((item, index) => {
                            const productName = getProductName(item);
                            const quantity = parseFloat(item.quantity) || 0;
                            const unitPrice = parseFloat(item.unit_price) || 0;
                            // Use order's GST rate (5%)
                            const gstRate = parseFloat(orderDetails.gst_rate) || 5;
                            // Calculate GST inclusive total
                            const totalWithoutGST = quantity * unitPrice;
                            const gstAmount = totalWithoutGST * (gstRate / 100);
                            const totalWithGST = totalWithoutGST + gstAmount;
                            
                            return (
                              <TableRow key={index}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">
                                    {productName}
                                  </Typography>
                                  {item.product?.code && (
                                    <Typography variant="caption" color="textSecondary" component="div">
                                      Code: {item.product.code}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {quantity} {item.uom || ''}
                                </TableCell>
                                <TableCell align="right">{formatCurrency(unitPrice)}</TableCell>
                                <TableCell align="right">{gstRate}%</TableCell>
                                <TableCell align="right">{formatCurrency(totalWithGST)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Payment History Section */}
      {selectedOrder && !loading && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Payment History
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {payments.length > 0 ? (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date & Time</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Settlement</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id} hover>
                        <TableCell>
                          {payment.payment_date ? format(new Date(payment.payment_date), 'dd/MM/yyyy, h:mm:ss a') : 'N/A'}
                        </TableCell>
                        <TableCell align="right" fontWeight="bold">
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={SETTLEMENT_LABELS[payment.settlement_type] || payment.settlement_type} 
                            size="small"
                            color={payment.settlement_type === 'full' ? 'success' : 'info'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={payment.payment_method?.replace('_', ' ').toUpperCase() || 'N/A'} 
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Payment Details">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => viewPaymentDetail(payment)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Print Receipt">
                            <IconButton 
                              size="small" 
                              color="secondary"
                              onClick={() => printPaymentReceipt(payment)}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Summary Section */}
              {orderDetails && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="textSecondary">
                        Total Amount (incl. GST)
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {formatCurrency(orderDetails.net_amount || orderDetails.total_amount || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="textSecondary">
                        Amount Paid
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatCurrency(orderDetails.amount_paid || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="textSecondary">
                        Due Amount
                      </Typography>
                      <Typography variant="h6" color="error">
                        {formatCurrency((orderDetails.net_amount || orderDetails.total_amount || 0) - (orderDetails.amount_paid || 0))}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" py={3}>
              <Typography variant="body1" color="textSecondary">
                No payment history found for this order.
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Select Order Prompt */}
      {!selectedOrder && !loading && !loadingOrders && orders.length > 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            Please select an order from the dropdown to view payment status and history.
          </Typography>
        </Paper>
      )}

      {/* No Orders Found */}
      {!loadingOrders && orders.length === 0 && !error && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No {orderType === 'sales' ? 'Sales' : 'Purchase'} orders found. 
            Please create an order first.
          </Typography>
        </Paper>
      )}

      {/* Payment Detail Dialog */}
      <Dialog 
        open={openPaymentDetailDialog} 
        onClose={() => setOpenPaymentDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            Payment Details
          </Typography>
          <IconButton onClick={() => setOpenPaymentDetailDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPaymentDetail && (
            <Box>
              {/* Payment Information */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#2E7D32' }}>
                Payment Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Payment Type:
                  </Typography>
                  <Typography variant="body1" component="div">
                    <Chip 
                      label={selectedPaymentDetail.payment_type?.toUpperCase() || 'N/A'} 
                      size="small"
                      color={selectedPaymentDetail.payment_type === 'received' ? 'success' : 'warning'}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Amount:
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {formatCurrency(selectedPaymentDetail.amount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Settlement Type:
                  </Typography>
                  <Typography variant="body1" component="div">
                    <Chip 
                      label={SETTLEMENT_LABELS[selectedPaymentDetail.settlement_type] || selectedPaymentDetail.settlement_type} 
                      size="small"
                      color={selectedPaymentDetail.settlement_type === 'full' ? 'success' : 'info'}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Payment Method:
                  </Typography>
                  <Typography variant="body1" component="div">
                    <Chip 
                      label={selectedPaymentDetail.payment_method?.replace('_', ' ').toUpperCase() || 'N/A'} 
                      size="small"
                      variant="outlined"
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Date & Time:
                  </Typography>
                  <Typography variant="body1">
                    {selectedPaymentDetail.payment_date ? format(new Date(selectedPaymentDetail.payment_date), 'dd/MM/yyyy, hh:mm:ss a') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Reference:
                  </Typography>
                  <Typography variant="body1">
                    {selectedPaymentDetail.reference_number || selectedPaymentDetail.reference || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Party:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedPaymentDetail.customer?.name || selectedPaymentDetail.supplier?.name || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Order Details */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#2E7D32' }}>
                Order Details
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Order No:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedPaymentDetail.order_no || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Order Type:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedPaymentDetail.is_sales_order ? 'Sales Order' : 'Purchase Order'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Order Value:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(selectedPaymentDetail.order_value || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Order Status:
                  </Typography>
                  <Typography variant="body1" component="div">
                    <Chip 
                      label={selectedPaymentDetail.order_status || 'N/A'} 
                      size="small"
                      color={getStatusColor(selectedPaymentDetail.order_status)}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Net Amount (incl. GST):
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(selectedPaymentDetail.net_amount || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Total Tax:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(selectedPaymentDetail.tax_amount || 0)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Balance at the time of this payment */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#2E7D32' }}>
                Balance at the time of this payment
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                {selectedPaymentDetail.payment_date ? format(new Date(selectedPaymentDetail.payment_date), 'dd/MM/yyyy, hh:mm a') : 'N/A'}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="textSecondary">
                    Cumulative Paid:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main">
                    {formatCurrency(selectedPaymentDetail.cumulative_amount_paid || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="textSecondary">
                    This Payment:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {formatCurrency(selectedPaymentDetail.amount || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="textSecondary">
                    Due Amount:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color={(selectedPaymentDetail.running_due_amount || 0) > 0 ? 'error' : 'success'}>
                    {formatCurrency(selectedPaymentDetail.running_due_amount || 0)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* GST Details */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#2E7D32' }}>
                GST Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    GST Rate:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    GST {selectedPaymentDetail.gst_rate_details?.rate || 5}%
                  </Typography>
                </Grid>
                {selectedPaymentDetail.cgst_amount > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      CGST:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatCurrency(selectedPaymentDetail.cgst_amount)}
                    </Typography>
                  </Grid>
                )}
                {selectedPaymentDetail.sgst_amount > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      SGST:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatCurrency(selectedPaymentDetail.sgst_amount)}
                    </Typography>
                  </Grid>
                )}
                {selectedPaymentDetail.igst_amount > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      IGST:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatCurrency(selectedPaymentDetail.igst_amount)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenPaymentDetailDialog(false)} color="primary">
            Close
          </Button>
          <Button 
            color="primary" 
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => {
              if (selectedPaymentDetail) {
                printPaymentReceipt(selectedPaymentDetail);
              }
            }}
          >
            Print Receipt
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PaymentStatus;